<template>
  <!-- Desktop Menu -->
  <nav class="hidden md:flex items-center justify-between bg-white border-b px-4 h-14 sticky top-0 z-50">
    <!-- Left: Logo + Menu Items -->
    <div class="flex items-center gap-1">
      <!-- Logo -->
      <img
        src="/src/assets/hotjet-logo.png"
        alt="Hotjet"
        class="h-10 object-contain cursor-pointer"
        @click="$emit('navigate', 'home')"
      />

      <!-- Chat Dropdown -->
      <div class="relative" ref="chatDropdownRef">
        <button
          @click="toggleDropdown('chat')"
          class="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition"
          :class="{ 'bg-gray-100': openDropdown === 'chat' }"
        >
          <span>💬</span>
          <span>Chat</span>
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div
          v-if="openDropdown === 'chat'"
          class="absolute left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border py-1 z-50"
        >
          <button
            @click="handleAction('chat')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>💬</span> Nový chat
          </button>
          <button
            @click="handleAction('history')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>📜</span> Historie
          </button>
        </div>
      </div>

      <!-- Cloud Dropdown (only for team admins) -->
      <div v-if="hasCloudAccess" class="relative" ref="cloudDropdownRef">
        <button
          @click="toggleDropdown('cloud')"
          class="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition"
          :class="{ 'bg-gray-100': openDropdown === 'cloud' }"
        >
          <span>☁️</span>
          <span>Cloud</span>
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div
          v-if="openDropdown === 'cloud'"
          class="absolute left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border py-1 z-50"
        >
          <button
            @click="handleAction('ozw')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>🏠</span> OZW
          </button>
          <button
            @click="handleAction('climatix')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>📊</span> Climatix
          </button>
          <button
            @click="handleAction('test')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>✅</span> Test
          </button>
          <button
            @click="handleAction('heating')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>🔥</span> Topení
          </button>
        </div>
      </div>

      <!-- Analýzy Dropdown -->
      <div v-if="hasAnalyzyAccess" class="relative" ref="analysisDropdownRef">
        <button
          @click="toggleDropdown('analysis')"
          class="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition"
          :class="{ 'bg-gray-100': openDropdown === 'analysis' }"
        >
          <span>📊</span>
          <span>Analýzy</span>
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div
          v-if="openDropdown === 'analysis'"
          class="absolute left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border py-1 z-50"
        >
          <button
            @click="handleAction('rvsAnalyzer')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>📈</span> RVS Logy
          </button>
          <button
            @click="handleAction('secAnalyzer')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>📉</span> SEC Logy
          </button>
          <button
            @click="handleAction('secCloudAnalyzer')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>📊</span> HP-POL/SEC
          </button>
        </div>
      </div>

      <!-- Výpočty Dropdown -->
      <div v-if="hasVypoctyAccess" class="relative" ref="calcDropdownRef">
        <button
          @click="toggleDropdown('calc')"
          class="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition"
          :class="{ 'bg-gray-100': openDropdown === 'calc' }"
        >
          <span>🔢</span>
          <span>Util</span>
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div
          v-if="openDropdown === 'calc'"
          class="absolute left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border py-1 z-50"
        >
          <button
            @click="handleAction('heatExchanger')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>🔥</span> Výměník tepla
          </button>
          <button
            @click="handleAction('noiseCalculator')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>🔊</span> Hluk TČ
          </button>
          <button
            @click="handleAction('contactorCalculator')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>⚡</span> Stykače a jističe
          </button>
          <button
            @click="handleAction('sheetMetalCalculator')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>🔩</span> Plechové díly
          </button>
          <button
            @click="handleAction('insulationCalculator')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>🧱</span> Izolace
          </button>
          <button
            @click="handleAction('secAlarmDecoder')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>🔔</span> SEC Alarmy
          </button>
          <button
            @click="handleAction('hotjetRender')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>🎨</span> AI Render
          </button>
          <button
            @click="handleAction('pdfTranslator')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>🌐</span> PDF Translator
          </button>
          <button
            @click="handleAction('pumpMatch')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>🔄</span> PumpMatch
          </button>
          <button
            @click="handleAction('partsViewer')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>🔧</span> 3D Dily
          </button>
          <button
            @click="handleAction('eprel')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>🔍</span> EPREL vyhledávač
          </button>
        </div>
      </div>

      <!-- Freshdesk Button -->
      <button
        v-if="hasFreshdeskAccess"
        @click="handleAction('freshdeskAnalyzer')"
        class="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition"
        title="Freshdesk Lite"
      >
        <span>🎫</span>
        <span>Freshdesk</span>
      </button>

      <!-- Mapa Button -->
      <button
        v-if="hasMapaAccess"
        @click="handleAction('serviceTechnicians')"
        class="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition"
        title="Mapa servisních techniků"
      >
        <span>🗺️</span>
        <span>Mapa</span>
      </button>

      <!-- Projekty Button -->
      <button
        v-if="hasProjektyAccess"
        @click="handleAction('projects')"
        class="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition"
        title="Správa projektů"
      >
        <span>📋</span>
        <span>Projekty</span>
      </button>
    </div>

    <!-- Right: Language + User Dropdown -->
    <div class="flex items-center gap-2">
      <!-- Language Selector -->
      <div class="relative" ref="langDropdownRef">
        <button
          @click.stop="toggleDropdown('lang')"
          class="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded transition"
        >
          <span>{{ currentLanguage }}</span>
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div
          v-if="openDropdown === 'lang'"
          class="absolute right-0 mt-1 w-24 bg-white rounded-lg shadow-lg border py-1 z-50"
        >
          <button
            v-for="lang in languages"
            :key="lang.code"
            @click.stop="selectLanguage(lang.code)"
            class="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100"
            :class="{ 'bg-gray-50 font-medium': language === lang.code }"
          >
            {{ lang.label }}
          </button>
        </div>
      </div>

      <!-- User Dropdown -->
      <div class="relative" ref="userDropdownRef">
        <button
          @click.stop="toggleDropdown('user')"
          class="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-100 rounded-lg transition"
        >
          <img
            v-if="user?.picture"
            :src="user.picture"
            :alt="user.name"
            class="w-7 h-7 rounded-full"
          />
          <span class="text-sm font-medium text-gray-700 hidden lg:inline">{{ user?.name }}</span>
          <span
            v-if="isAdmin"
            class="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded hidden lg:inline"
          >
            Admin
          </span>
          <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <!-- User Dropdown Menu -->
        <div
          v-if="openDropdown === 'user'"
          class="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border py-1 z-50"
        >
          <!-- User Info -->
          <div class="px-3 py-2 border-b">
            <div class="text-sm font-medium text-gray-700">{{ user?.name }}</div>
            <div class="text-xs text-gray-500">{{ user?.email }}</div>
          </div>

          <!-- Admin Button -->
          <button
            v-if="isAdmin"
            @click.stop="handleAction('admin')"
            class="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Admin Panel
          </button>

          <button
            v-if="hasModels3dAccess"
            @click.stop="handleAction('modelsAdmin')"
            class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>📦</span> 3D Modely
          </button>

          <div v-if="isAdmin" class="border-t border-gray-100 my-1"></div>

          <!-- Logout Button -->
          <button
            @click.stop="handleLogout"
            class="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Odhlásit se
          </button>
        </div>
      </div>
    </div>
  </nav>

  <!-- Mobile Menu -->
  <nav class="md:hidden bg-white border-b px-3 h-14 sticky top-0 z-50 flex items-center justify-between">
    <!-- Hamburger -->
    <button
      @click="mobileMenuOpen = true"
      class="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    </button>

    <!-- Logo (center) -->
    <img
      src="/src/assets/hotjet-logo.png"
      alt="Hotjet"
      class="h-8"
      @click="$emit('navigate', 'home')"
    />

    <!-- User Avatar -->
    <img
      v-if="user?.picture"
      :src="user.picture"
      :alt="user.name"
      class="w-8 h-8 rounded-full"
    />
    <div v-else class="w-8 h-8 bg-gray-200 rounded-full"></div>
  </nav>

  <!-- Mobile Drawer -->
  <Teleport to="body">
    <div
      v-if="mobileMenuOpen"
      class="fixed inset-0 z-50 md:hidden"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black bg-opacity-50"
        @click="mobileMenuOpen = false"
      ></div>

      <!-- Drawer -->
      <div class="absolute left-0 top-0 h-full w-72 bg-white shadow-xl overflow-y-auto flex flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b">
          <img src="/src/assets/hotjet-logo.png" alt="Hotjet" class="h-8" />
          <button
            @click="mobileMenuOpen = false"
            class="p-1 text-gray-500 hover:text-gray-700"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Menu Items -->
        <div class="py-2 flex-1">
          <!-- Chat Section -->
          <div class="border-b pb-2 mb-2">
            <button
              @click="toggleMobileSection('chat')"
              class="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
            >
              <span class="flex items-center gap-2 font-medium">
                <span>💬</span> Chat
              </span>
              <svg
                class="w-4 h-4 transition-transform"
                :class="{ 'rotate-180': mobileOpenSection === 'chat' }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div v-if="mobileOpenSection === 'chat'" class="bg-gray-50 py-1">
              <button
                @click="handleMobileAction('chat')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                💬 Nový chat
              </button>
              <button
                @click="handleMobileAction('history')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                📜 Historie
              </button>
            </div>
          </div>

          <!-- Cloud Section (team admins only) -->
          <div v-if="hasCloudAccess" class="border-b pb-2 mb-2">
            <button
              @click="toggleMobileSection('cloud')"
              class="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
            >
              <span class="flex items-center gap-2 font-medium">
                <span>☁️</span> Cloud
              </span>
              <svg
                class="w-4 h-4 transition-transform"
                :class="{ 'rotate-180': mobileOpenSection === 'cloud' }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div v-if="mobileOpenSection === 'cloud'" class="bg-gray-50 py-1">
              <button
                @click="handleMobileAction('ozw')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                🏠 OZW
              </button>
              <button
                @click="handleMobileAction('climatix')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                📊 Climatix
              </button>
              <button
                @click="handleMobileAction('test')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                ✅ Test
              </button>
              <button
                @click="handleMobileAction('heating')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                🔥 Topení
              </button>
            </div>
          </div>

          <!-- Analýzy Section -->
          <div v-if="hasAnalyzyAccess" class="border-b pb-2 mb-2">
            <button
              @click="toggleMobileSection('analysis')"
              class="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
            >
              <span class="flex items-center gap-2 font-medium">
                <span>📊</span> Analýzy
              </span>
              <svg
                class="w-4 h-4 transition-transform"
                :class="{ 'rotate-180': mobileOpenSection === 'analysis' }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div v-if="mobileOpenSection === 'analysis'" class="bg-gray-50 py-1">
              <button
                @click="handleMobileAction('rvsAnalyzer')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                📈 RVS Logy
              </button>
              <button
                @click="handleMobileAction('secAnalyzer')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                📉 SEC Logy
              </button>
              <button
                @click="handleMobileAction('secCloudAnalyzer')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                📊 HP-POL/SEC
              </button>
            </div>
          </div>

          <!-- Výpočty Section -->
          <div v-if="hasVypoctyAccess" class="border-b pb-2 mb-2">
            <button
              @click="toggleMobileSection('calc')"
              class="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
            >
              <span class="flex items-center gap-2 font-medium">
                <span>🔢</span> Výpočty
              </span>
              <svg
                class="w-4 h-4 transition-transform"
                :class="{ 'rotate-180': mobileOpenSection === 'calc' }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div v-if="mobileOpenSection === 'calc'" class="bg-gray-50 py-1">
              <button
                @click="handleMobileAction('heatExchanger')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                🔥 Výměník tepla
              </button>
              <button
                @click="handleMobileAction('noiseCalculator')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                🔊 Hluk TČ
              </button>
              <button
                @click="handleMobileAction('contactorCalculator')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                ⚡ Stykače a jističe
              </button>
              <button
                @click="handleMobileAction('sheetMetalCalculator')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                🔩 Plechové díly
              </button>
              <button
                @click="handleMobileAction('insulationCalculator')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                🧱 Izolace
              </button>
              <button
                @click="handleMobileAction('secAlarmDecoder')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                🔔 SEC Alarmy
              </button>
              <button
                @click="handleMobileAction('hotjetRender')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                🎨 AI Render
              </button>
              <button
                @click="handleMobileAction('pdfTranslator')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                🌐 PDF Translator
              </button>
              <button
                @click="handleMobileAction('pumpMatch')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                🔄 PumpMatch
              </button>
              <button
                @click="handleMobileAction('partsViewer')"
                class="w-full px-8 py-2 text-left text-sm hover:bg-gray-100"
              >
                🔧 3D Dily
              </button>
            </div>
          </div>

          <!-- Freshdesk Button -->
          <div v-if="hasFreshdeskAccess" class="border-b pb-2 mb-2">
            <button
              @click="handleMobileAction('freshdeskAnalyzer')"
              class="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 font-medium"
            >
              <span>🎫</span> Freshdesk
            </button>
          </div>

          <!-- Mapa Button -->
          <div v-if="hasMapaAccess" class="border-b pb-2 mb-2">
            <button
              @click="handleMobileAction('serviceTechnicians')"
              class="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 font-medium"
            >
              <span>🗺️</span> Mapa
            </button>
          </div>

          <!-- Projekty Button -->
          <div v-if="hasProjektyAccess" class="border-b pb-2 mb-2">
            <button
              @click="handleMobileAction('projects')"
              class="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 font-medium"
            >
              <span>📋</span> Projekty
            </button>
          </div>

          <!-- User Section -->
          <div class="px-4 py-3">
            <div class="flex items-center gap-3 mb-3">
              <img
                v-if="user?.picture"
                :src="user.picture"
                :alt="user.name"
                class="w-10 h-10 rounded-full"
              />
              <div>
                <div class="font-medium">{{ user?.name }}</div>
                <div class="text-xs text-gray-500">{{ user?.email }}</div>
              </div>
            </div>

            <!-- Admin Button -->
            <button
              v-if="isAdmin"
              @click="handleMobileAction('admin')"
              class="w-full mb-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-100 transition"
            >
              ⚙️ Admin Panel
            </button>

            <button
              v-if="hasModels3dAccess"
              @click="handleMobileAction('modelsAdmin')"
              class="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100"
            >
              📦 3D Modely
            </button>

            <!-- Logout Button -->
            <button
              @click="$emit('logout'); mobileMenuOpen = false"
              class="w-full px-4 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition"
            >
              Odhlásit se
            </button>
          </div>

          <!-- Language Section (at bottom) -->
          <div class="border-t mt-auto px-4 py-3">
            <div class="flex justify-center">
              <div class="flex gap-2">
                <button
                  v-for="lang in languages"
                  :key="lang.code"
                  @click="selectLanguage(lang.code)"
                  class="px-3 py-1.5 text-sm rounded-lg border transition"
                  :class="language === lang.code
                    ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                    : 'bg-white border-gray-300 hover:bg-gray-50'"
                >
                  {{ lang.label }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  user: {
    type: Object,
    required: true
  },
  language: {
    type: String,
    default: 'cs'
  }
});

const emit = defineEmits(['navigate', 'logout', 'languageChange', 'toggleHistory']);

// Dropdown state
const openDropdown = ref(null);
const mobileMenuOpen = ref(false);
const mobileOpenSection = ref(null);
const userDropdownRef = ref(null);

// Language options
const languages = [
  { code: 'cs', label: 'CZ' },
  { code: 'de', label: 'DE' },
  { code: 'en', label: 'EN' }
];

const currentLanguage = computed(() => {
  const lang = languages.find(l => l.code === props.language);
  return lang ? lang.label : 'CZ';
});

// Permission checks (DB-based roles + modules)
const isAdmin = computed(() => {
  return ['admin', 'superadmin'].includes(props.user?.role);
});

// Module-based access (admin/superadmin bypass all checks)
function userHasModule(mod) {
  if (isAdmin.value) return true;
  const perms = props.user?.permissions;
  if (!perms) return false;
  return perms[mod] === true;
}

const hasCloudAccess = computed(() => userHasModule('cloud'));
const hasAnalyzyAccess = computed(() => userHasModule('analyzy'));
const hasVypoctyAccess = computed(() => userHasModule('vypocty'));
const hasFreshdeskAccess = computed(() => userHasModule('freshdesk'));
const hasMapaAccess = computed(() => userHasModule('mapa'));
const hasProjektyAccess = computed(() => userHasModule('projekty'));
const hasModels3dAccess = computed(() => userHasModule('models3d'));

// Dropdown handlers
function toggleDropdown(name) {
  openDropdown.value = openDropdown.value === name ? null : name;
}

function closeDropdowns() {
  openDropdown.value = null;
}

function toggleMobileSection(name) {
  mobileOpenSection.value = mobileOpenSection.value === name ? null : name;
}

// Action handlers
function handleAction(action) {
  closeDropdowns();
  if (action === 'history') {
    emit('toggleHistory');
  } else {
    emit('navigate', action);
  }
}

function handleMobileAction(action) {
  mobileMenuOpen.value = false;
  mobileOpenSection.value = null;
  if (action === 'history') {
    emit('toggleHistory');
  } else {
    emit('navigate', action);
  }
}

function selectLanguage(code) {
  closeDropdowns();
  emit('languageChange', code);
}

function handleLogout() {
  closeDropdowns();
  emit('logout');
}

// Click outside to close dropdowns
function handleClickOutside(event) {
  if (!event.target.closest('.relative')) {
    closeDropdowns();
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>
